import React from 'react';
import { Link } from 'react-router-dom';
import { Radio } from 'antd';

import TabLoadState from '../common/TabLoadState';

import { PRIVATE_NOTE } from './TagBadge';
import {
	referenceCleanupWords,
	referenceHolderLabel,
	referenceKey,
	referenceName,
	referenceOffersCleanup,
} from '../../utils/itemTagSettings';
import { EXTERNAL_LINK_PROPS } from '../common/ExternalLink';

/**
 * THE IMPACT REPORT (docs/plans/tags.md §3.2, ruled d33, d37, d38; build step
 * T4d): what refers to a tag about to be deleted, each entry with a link and
 * a choice — **Remove for me** or **Leave it** — as the API lists it
 * (services/tagReferences.js, keepr-api utils/tagReferences.js). It is shown
 * before EVERY deletion: a tag's Delete… (confirmTagDelete), an item tag
 * turned off with both kinds of application removed (StopItemTagModal), and a
 * card's item tags turned off the same way (ItemTagModeModal).
 *
 * - The API decides what is listed: the person's OWN pins and personal
 *   filters, layouts, notifications and multi add forms, and — to a manager of
 *   the collection each lives in — the collection's filters, rules,
 *   notifications, worklists, layout blocks, driven filters, multi add seeds
 *   and saved exports (owner calls B14, B15). Never another person's (d37):
 *   the report says theirs stop matching, never whose.
 * - "Remove for me" never edits a query: a pin is unpinned, a filter or a
 *   page layout deleted whole, a block removed whole, a rule, notification or
 *   saved export paused (d37). An entry that offers none (a worklist, a driven
 *   filter, a multi add seed — each lives inside a card or a form) has only
 *   its link.
 * - A STRICT lookup's filter (`blocksDelete`, the owner's ruling b80) says
 *   **Blocks deleting**: the tag's delete is refused while it names the tag,
 *   so the person edits the filter first (the link opens the card or set).
 *   The API's refusal still decides (components/tags/TagRefusal.js).
 * - ⚑ Every entry starts at **Leave it**: nothing is removed unless the
 *   person chooses it. ⚑ Its link opens in a new tab, so the decision in
 *   hand stays open.
 *
 * A wait is the standard skeleton and a failed read is an error with Retry
 * (`onRetry`, when the caller can read again). Controlled: `choices` is `{ [holder:id]: 'remove' | 'leave' }` and
 * `onChange(key, value)` sets one. The caller turns them into the request's
 * `cleanup` (utils/itemTagSettings.js cleanupFor).
 */
export default function ImpactReport({
	references = [],
	truncated = false,
	loading = false,
	error = null,
	onRetry,
	choices = {},
	onChange,
	disabled = false,
	subject = 'it',
}) {
	const list = Array.isArray(references) ? references : [];
	return (
		<section className="impact-report" aria-label="What refers to it" data-testid="impact-report">
			<div className="micro-label impact-report__kicker">What refers to {subject}</div>
			{loading || error ? (
				<TabLoadState
					loading={loading}
					error={error}
					label="what refers to it"
					errorDetail="Pins, filters and rules that name it will stop matching. Try again to see them first."
					onRetry={onRetry}
					rows={2}
				/>
			) : null}
			{!loading && !error && !list.length ? (
				<p className="impact-report__none">Nothing you can see refers to {subject}: no pins, filters or rules.</p>
			) : null}
			{!loading && list.length ? (
				<ul className="impact-report__list">
					{list.map(entry => {
						const key = referenceKey(entry);
						const name = referenceName(entry);
						const offers = referenceOffersCleanup(entry);
						const choice = choices[key] === 'remove' && offers ? 'remove' : 'leave';
						return (
							<li key={key} className="impact-report__entry" data-reference={key}>
								<div className="impact-report__head">
									<span className="impact-report__kind">{referenceHolderLabel(entry)}</span>
									{entry.link ? (
										<Link to={entry.link} {...EXTERNAL_LINK_PROPS} className="impact-report__name">{name}</Link>
									) : (
										<span className="impact-report__name">{name}</span>
									)}
									{entry.mine ? <span className="impact-report__mine">{PRIVATE_NOTE}</span> : null}
									{entry.blocksDelete ? <span className="impact-report__blocks">Blocks deleting</span> : null}
								</div>
								{entry.blocksDelete ? (
									<p className="impact-report__effect">
										It only offers items with {subject}, so {subject} can’t be deleted until its filter is edited. Open it to change the filter.
									</p>
								) : offers ? (
									<>
										<div role="radiogroup" aria-label={`What to do with ${name}`} className="tag-choice">
											<Radio.Group
												className="impact-report__choice"
												optionType="button"
												size="small"
												value={choice}
												disabled={disabled}
												onChange={event => onChange?.(key, event.target.value)}
												options={[
													{ label: 'Remove for me', value: 'remove' },
													{ label: 'Leave it', value: 'leave' },
												]}
											/>
										</div>
										<p className="impact-report__effect">
											{choice === 'remove' ? referenceCleanupWords(entry) : 'It stays, and stops matching anything.'}
										</p>
									</>
								) : (
									<p className="impact-report__effect">It stays, and stops matching anything. It lives inside a card or a form: open it to change it.</p>
								)}
							</li>
						);
					})}
				</ul>
			) : null}
			{!loading && truncated ? <p className="impact-report__effect">Only the first 500 are listed.</p> : null}
			<p className="meta-line impact-report__others">
				Other people’s pins and personal filters that name {subject} stop matching too. They are not listed here.
			</p>
		</section>
	);
}
